/**
 * AppErrorBoundary — v.1-D §7.17, §15.3.
 *
 * Outermost error boundary of the provider tree. Catches anything that
 * escapes the per-route `RouteErrorBoundary` (e.g. provider-construction
 * errors, lazy-module load failures that fall through Suspense). Renders a
 * full-screen 500 card with a Reload button — distinct from the per-route
 * "Page error" Panel which preserves the surrounding chrome.
 *
 * Logging goes through the structured `logger` per §9.2 forbidden patterns
 * (no direct `console.*` in `src/**`).
 */
import React from 'react';
import { logger } from '../lib/logger';

interface AppErrorBoundaryState {
  hasError: boolean;
  message: string;
}

export class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logger.error('app_error_boundary_caught', {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: 32,
            background: 'var(--canvas)',
          }}
        >
          <div
            style={{
              border: '1px solid var(--lightest-gray)',
              borderRadius: 'var(--radius-signature)',
              padding: 48,
              maxWidth: 480,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 48,
                marginBottom: 12,
              }}
            >
              500
            </div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 0.7,
                color: 'var(--muted)',
                marginBottom: 16,
              }}
            >
              Application error
            </div>
            <div
              style={{
                fontSize: 14,
                color: 'var(--muted)',
                marginBottom: 24,
              }}
            >
              {this.state.message}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--ink)',
                color: 'var(--canvas)',
                fontSize: 14,
                cursor: 'pointer',
                border: 'none',
              }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
